import React, { useState, useEffect } from 'react';
import { Search, User, Mail, X } from 'lucide-react';
import axios from 'axios';
import { useTenantProtection } from '../../../hooks/useTenantProtection';

const LineUserSelector = ({ value, onChange, multiple = true, required = false }) => {
  const { getProtectedTenantCode } = useTenantProtection();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    loadLineUsers();
  }, []);

  useEffect(() => {
    // Initialize selected users from value
    if (value) {
      if (Array.isArray(value)) {
        setSelectedUsers(value);
      } else if (typeof value === 'string') {
        // Handle comma-separated string
        const ids = value.split(',').map(id => id.trim()).filter(Boolean);
        setSelectedUsers(ids);
      }
    }
  }, [value]);

  useEffect(() => {
    // Filter users based on search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      const filtered = users.filter(user =>
        user.fullName.toLowerCase().includes(term) ||
        user.email.toLowerCase().includes(term) ||
        user.lineUserId.toLowerCase().includes(term)
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users);
    }
  }, [searchTerm, users]);

  const loadLineUsers = async () => {
    try {
      setLoading(true);
      const tenantCode = getProtectedTenantCode();
      console.log('[LineUserSelector] Loading LINE users for tenant:', tenantCode);
      
      const response = await axios.get('/api/automation/line-users', {
        headers: { 'X-Tenant-Code': tenantCode },
      });
      
      console.log('[LineUserSelector] Response:', response.data);
      
      if (response.data.success) {
        console.log('[LineUserSelector] Loaded users:', response.data.users.length, 'LINE users');
        setUsers(response.data.users);
        setFilteredUsers(response.data.users);
      } else {
        console.warn('[LineUserSelector] API returned success:false');
      }
    } catch (error) {
      console.error('[LineUserSelector] Failed to load LINE users:', error);
      console.error('[LineUserSelector] Error response:', error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = (lineUserId) => {
    let newSelection;
    
    if (multiple) {
      // Multiple selection
      if (selectedUsers.includes(lineUserId)) {
        newSelection = selectedUsers.filter(id => id !== lineUserId);
      } else {
        newSelection = [...selectedUsers, lineUserId];
      }
    } else {
      // Single selection
      newSelection = [lineUserId];
      setShowDropdown(false);
    }
    
    setSelectedUsers(newSelection);
    if (onChange) {
      onChange(newSelection);
    }
  };

  const handleRemoveUser = (lineUserId, e) => {
    e.stopPropagation();
    const newSelection = selectedUsers.filter(id => id !== lineUserId);
    setSelectedUsers(newSelection);
    if (onChange) {
      onChange(newSelection);
    }
  };

  const getSelectedUserDetails = (lineUserId) => {
    return users.find(u => u.lineUserId === lineUserId);
  };

  return (
    <div className="space-y-2">
      {/* Selected Users Display */}
      {selectedUsers.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {selectedUsers.map(lineUserId => {
            const user = getSelectedUserDetails(lineUserId);
            return (
              <div
                key={lineUserId}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-sm"
              >
                <User className="h-3 w-3" />
                <span className="font-medium">
                  {user ? user.fullName : lineUserId}
                </span>
                {user && (
                  <span className="text-xs opacity-75">
                    ({lineUserId})
                  </span>
                )}
                <button
                  type="button"
                  onClick={(e) => handleRemoveUser(lineUserId, e)}
                  className="ml-1 hover:text-purple-900 dark:hover:text-purple-100"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Search and Dropdown */}
      <div className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => setShowDropdown(true)}
            placeholder={`Search LINE users... ${selectedUsers.length > 0 ? `(${selectedUsers.length} selected)` : ''}`}
            className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:border-purple-500 focus:ring-purple-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 sm:text-sm"
          />
        </div>

        {/* Dropdown */}
        {showDropdown && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-10"
              onClick={() => setShowDropdown(false)}
            />

            {/* Dropdown Menu */}
            <div className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto">
              {loading ? (
                <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-center">
                  Loading LINE users...
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-center">
                  {searchTerm ? 'No users found matching your search' : 'No LINE users available'}
                </div>
              ) : (
                <div className="py-1">
                  {filteredUsers.map(user => {
                    const isSelected = selectedUsers.includes(user.lineUserId);
                    return (
                      <button
                        key={user.lineUserId}
                        type="button"
                        onClick={() => handleUserSelect(user.lineUserId)}
                        className={`
                          w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors
                          ${isSelected ? 'bg-purple-50 dark:bg-purple-900/20' : ''}
                        `}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className={`
                              w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                              ${isSelected 
                                ? 'bg-purple-500 text-white' 
                                : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                              }
                            `}>
                              {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                            </div>
                            <div>
                              <div className={`text-sm font-medium ${isSelected ? 'text-purple-700 dark:text-purple-300' : 'text-gray-900 dark:text-gray-100'}`}>
                                {user.fullName}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center space-x-2">
                                <span className="flex items-center">
                                  <Mail className="h-3 w-3 mr-1" />
                                  {user.email}
                                </span>
                                <span className="text-gray-400 dark:text-gray-500">•</span>
                                <span className="font-mono">{user.lineUserId}</span>
                              </div>
                            </div>
                          </div>
                          {isSelected && (
                            <div className="text-purple-500 dark:text-purple-400">✓</div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Helper Text */}
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
        {multiple 
          ? 'Click to select multiple users. Selected users will be added as LINE recipients.'
          : 'Click to select a user. LINE User ID will be used as recipient.'
        }
      </p>

      {/* Manual Input Option */}
      <details className="mt-2">
        <summary className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300">
          Or enter LINE User IDs manually
        </summary>
        <div className="mt-2">
          <textarea
            value={Array.isArray(selectedUsers) ? selectedUsers.join(', ') : selectedUsers}
            onChange={(e) => {
              const ids = e.target.value.split(',').map(id => id.trim()).filter(Boolean);
              setSelectedUsers(ids);
              if (onChange) {
                onChange(ids);
              }
            }}
            rows={2}
            placeholder="Uxxxxxxxxxxxxx, Uyyyyyyyyyyyyy"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:border-purple-500 focus:ring-purple-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 sm:text-sm font-mono"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Enter LINE User IDs separated by commas
          </p>
        </div>
      </details>
    </div>
  );
};

export default LineUserSelector;

